import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import groupBy from 'lodash/groupBy';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { StyledAvatar } from '../../../StyledAvatar/StyledAvatar';
import {
    RoleHeader,
    RoleTitle,
    AddShiftText,
    ShiftCard,
    ShiftInfo,
    ShiftRight,
    TimeContainer,
    TextContent,
    StatusText,
    ShiftListWrapper,
    NoShiftsContainer,
    NoShiftsText,
    RoleSectionWrapper,
} from './ShiftDetailsPanel.styles';
import { setSelectedShift } from '../../../../redux/shifts/shiftsSlice';
import { DRAWER_TYPES } from '../../../../pages/Dashboard/dashboardDrawerConfig';
import { SHIFT_INFORMATION_TITLE } from '../../../../utils/constants/shiftConstants';
import { selectAllShiftTypes } from '../../../../redux/slices/shiftTypesSlice';
import { showErrorToast } from '../../../../utils/toastUtils';
import { useTimeFormatters } from '../../../../utils/Hooks/venue/useVenueTimeFormatters';

const DailyShiftsList = ({ shifts, handleOpenDrawer, selectedDay, isEmployeeView = false, currentUserId }) => {
    const dispatch = useDispatch();
    const shiftTypes = useSelector(selectAllShiftTypes);
    const { formatTime } = useTimeFormatters();

    // Process shifts data
    const processedShifts = useMemo(() => {
        return shifts.map(shift => {

            const employeeInfo = shift.employee || {};
            const name = shift.assignedName || employeeInfo.displayName || 'Unassigned';
            const profileImage = shift.assignedProfileImageUrl || employeeInfo.profileImageUrl;
            const initials = name
                .split(' ')
                .map((n) => n[0])
                .join('');

            const start = shift.clockedInAt || shift.start;
            const end = shift.clockedOutAt || shift.end;
            const timeRange = `${formatTime(start)} - ${formatTime(end)}`;
            const missed = !shift.clockedInAt && !shift.clockedOutAt && dayjs().isAfter(end);

            // Check if this is the current user's shift
            const isCurrentUserShift = currentUserId && (shift.eid === currentUserId);

            // Check if the shift is late
            let isLate = false;
            let lateDuration = 0;

            // Case 1: Already clocked in late
            if (shift.clockedInAt && dayjs(shift.clockedInAt).isAfter(shift.start)) {
                isLate = true;
                lateDuration = dayjs(shift.clockedInAt).diff(shift.start, 'minute');

                // Case 2: Not clocked in and shift already started
            } else if (!shift.clockedInAt && dayjs().isAfter(shift.start)) {
                isLate = true;
                lateDuration = dayjs().diff(shift.start, 'minute');
            }

            return {
                ...shift,
                displayName: name,
                profileImage,
                initials,
                timeRange,
                missed,
                isLate,
                lateDuration,
                isCurrentUserShift
            };
        });
    }, [shifts, currentUserId, formatTime]);

    // Group shifts by role
    const shiftsByRole = useMemo(() => {
        return groupBy(processedShifts, 'name');
    }, [processedShifts]);

    const handleRoleAddShift = useCallback((roleName) => {
        const matchingShiftType = shiftTypes.find((type) => type.name === roleName);
        if (matchingShiftType) {
            // Open AddShiftForm with both date and role pre-selected
            handleOpenDrawer(DRAWER_TYPES.ADD_SHIFT, {
                selectedDate: new Date(selectedDay),
                dateFromShiftButton: selectedDay,
                preSelectedRole: matchingShiftType,
            });
        } else {
            showErrorToast(`Could not open shift form for role: ${roleName}. Please check the role name.`);
        }
    }, [shiftTypes, handleOpenDrawer, selectedDay]);

    const handleShiftClick = useCallback(
        (shift) => {
            dispatch(setSelectedShift(shift));
            handleOpenDrawer(DRAWER_TYPES.SHIFT_INFORMATION, { shift }, SHIFT_INFORMATION_TITLE(shift));
        },
        [dispatch, handleOpenDrawer]
    );

    if (!processedShifts || processedShifts.length === 0) {
        return (
            <NoShiftsContainer>
                <NoShiftsText variant="body1">
                    No shifts scheduled for this day.
                </NoShiftsText>
            </NoShiftsContainer>
        );
    }

    return (
        <ShiftListWrapper>
            {Object.entries(shiftsByRole).map(([roleName, roleShifts]) => (
                <RoleSectionWrapper key={roleName}>
                    <RoleHeader>
                        <RoleTitle variant="subtitle2">{roleName}</RoleTitle>
                        {!isEmployeeView && (
                            <AddShiftText variant="body2" onClick={() => handleRoleAddShift(roleName)}>
                                Add shift
                            </AddShiftText>
                        )}
                    </RoleHeader>

                    {roleShifts.map((shift) => (
                        <ShiftCard
                            key={shift.id}
                            onClick={() => handleShiftClick(shift)}
                            highlighted={shift.isCurrentUserShift}>
                            <ShiftInfo>
                                <StyledAvatar
                                    size="small"
                                    alt={shift.displayName}
                                    src={shift.profileImage}
                                    initials={shift.initials}
                                />
                                <TextContent>
                                    <Typography variant="body2">{shift.displayName}</Typography>
                                    <TimeContainer>
                                        <Typography variant="caption">{shift.timeRange}</Typography>
                                        {shift.missed ? (
                                            <StatusText>Missed shift</StatusText>
                                        ) : shift.isLate && (
                                            <StatusText>
                                                {shift.lateDuration >= 60
                                                    ? `${Math.floor(shift.lateDuration / 60)}hr ${shift.lateDuration % 60}min late`
                                                    : `${shift.lateDuration}min late`}
                                            </StatusText>
                                        )}
                                    </TimeContainer>
                                </TextContent>
                            </ShiftInfo>
                            <ShiftRight>
                                <ArrowForwardIosIcon />
                            </ShiftRight>
                        </ShiftCard>
                    ))}
                </RoleSectionWrapper>
            ))}
        </ShiftListWrapper>
    );
};

export default DailyShiftsList;